import { useAuth } from "@clerk/clerk-react"
import { useState, useEffect } from "react"
import CreateFetchInit from "../utils/create-fetch-init"
import { endpoints } from "../app/api/endpoints"

type AccessStatus = "loading" | "granted" | "denied" | "error"

export function usePlenumAccess() {
    const { isSignedIn, isLoaded, getToken, userId, sessionId } = useAuth()
    const [status, setStatus] = useState<AccessStatus>("loading")
    const [checkedIdentity, setCheckedIdentity] = useState<string>()
    const [attempt, setAttempt] = useState(0)
    const identity = JSON.stringify([userId, sessionId, isSignedIn])

    useEffect(() => {
        let cancelled = false
        const updateStatus = (value: AccessStatus) => {
            if (!cancelled) {
                setCheckedIdentity(identity)
                setStatus(value)
            }
        }
        if (!isLoaded) {
            return
        }

        if (!isSignedIn) {
            updateStatus("granted") // Not signed in, let public routes work
            return
        }

        updateStatus("loading")

        const checkAccess = async () => {
            try {
                const token = await getToken()
                if (cancelled) return
                if (!token) {
                    updateStatus("error")
                    return
                }

                const response = await fetch(
                    endpoints.usersMe,
                    CreateFetchInit("GET", undefined, token),
                )

                if (response.ok) {
                    updateStatus("granted")
                } else if (response.status === 403) {
                    updateStatus("denied")
                } else {
                    updateStatus("error")
                }
            } catch (error) {
                console.error("Failed to verify access:", error)
                updateStatus("error")
            }
        }

        checkAccess()
        return () => {
            cancelled = true
        }
    }, [isLoaded, isSignedIn, getToken, identity, attempt])

    const currentStatus = isLoaded && checkedIdentity === identity ? status : "loading"
    return {
        accessDenied: currentStatus === "denied",
        accessGranted: currentStatus === "granted",
        isLoading: currentStatus === "loading",
        accessError: currentStatus === "error",
        retry: () => {
            setStatus("loading")
            setAttempt((value) => value + 1)
        },
    }
}
